/**
 * RoomRequestFacts - the ONE renderer for a parsed room request, everywhere
 * one can appear.
 *
 * Bobbie (2026-08-02), reading a room request in her inbox: "gibberish....
 * clean up. Use color. When, Where, What, Why, WHO needs to be super clear."
 * The bug: lib/roomRequests.ts rides the request's facts inside a to-do's
 * `description` string - a human summary FIRST, then a small parseable
 * machine block below it (ROOM_REQUEST_MARKER onward: raw Firestore doc ids,
 * ISO timestamps, a raw uid). That block exists so the request survives being
 * a plain task under the deployed rules (see roomRequests.ts's file doc for
 * why a whole new collection was not an option). Every surface that printed
 * `task.description` as plain text - Tasks.tsx, Messages.tsx, the home task
 * summary - was printing that block straight at a human, and printing the
 * human summary AND the machine block meant the same five facts twice.
 *
 * Fix, chosen deliberately over moving the payload into structured task
 * fields: the description keeps carrying the payload (parseRoomRequestTask
 * already round-trips it, tests already pin that round trip), and every
 * surface that shows a room request renders THIS component from the parsed
 * RoomRequestDetails instead of the raw string. lib/roomRequests.ts's
 * `stripRoomRequestPayload()` is the second line of defense for any surface
 * that still prints a bare description - a shared helper, not a rule a new
 * screen has to remember. Moving to structured fields would touch
 * shared/firestore.rules while the rules deploy pipeline is only one day back
 * from broken (see HANDOFF.md 2026-08-01/02) - not worth the risk for a
 * rendering bug.
 *
 * No format change means no v1/v2 split: every pending request today already
 * parses with parseRoomRequestTask and renders correctly here, the same way
 * a brand new one would.
 *
 * Facts render in Bobbie's stated order - WHEN, WHERE, WHAT, WHY, WHO - each
 * with its own colored micro-label so the five are scannable at a glance
 * without reading the whole card. WHEN gets extra size: it is the most
 * prominent fact after the request's own status (pending/handled/declined),
 * which the caller renders around this component. Colors are the app's own
 * text-safe tokens (see theme.css's contrast block up top) - no new hex, no
 * brown anywhere.
 */
import type React from 'react';
import type { RoomRequestDetails, RoomRequestKind } from '../lib/roomRequests';
import { roomRequestWhenLabel } from '../lib/roomRequests';

const KIND_WORDS: Record<RoomRequestKind, string> = {
  class: 'Class time',
  rehearsal: 'Rehearsal',
  event: 'Event',
  block: 'Room block',
};

/** "Kim teacher test" -> "Kim". Falls back to the whole name if it is one word. */
function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

function Fact({
  label,
  color,
  dense,
  emphasize,
  children,
}: {
  label: string;
  color: string;
  dense: boolean;
  emphasize?: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div style={{ display: 'flex', gap: dense ? 6 : 8, alignItems: 'flex-start' }}>
      <span
        style={{
          flexShrink: 0,
          width: dense ? 42 : 52,
          paddingTop: 1,
          fontSize: dense ? 9 : 10,
          fontWeight: 900,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color,
        }}
      >
        {label}
      </span>
      <span
        style={{
          minWidth: 0,
          fontSize: emphasize ? (dense ? 13 : 15) : dense ? 12 : 13.5,
          fontWeight: emphasize ? 900 : 700,
          color: 'var(--ink)',
          lineHeight: 1.4,
        }}
      >
        {children}
      </span>
    </div>
  );
}

export interface RoomRequestFactsProps {
  details: RoomRequestDetails;
  /** Live room name when the caller already has rooms loaded (falls back to
   * the name captured when the request was sent - never the room id). */
  roomName?: string;
  offeringName?: string | null;
  /** Tighter type for close quarters, like the home task-summary row. */
  dense?: boolean;
  /** The one-line "go do this" instruction. Off on /calendar/requests itself
   * - a Director reading this there is already at that exact door. */
  showInstruction?: boolean;
  /** Surface-specific next step. Tasks can approve in place, while older
   * read-only surfaces still point to the full room-request desk. */
  instruction?: React.ReactNode;
  /** Surface-specific truth beneath WHERE for a claimed-but-open request. */
  whereNote?: React.ReactNode;
}

export function RoomRequestFacts({
  details,
  roomName,
  offeringName,
  dense = false,
  showInstruction = true,
  instruction,
  whereNote,
}: RoomRequestFactsProps) {
  const resolvedRoomName = roomName ?? details.roomName;
  const kindWords = KIND_WORDS[details.kind] ?? 'Event';
  const requesterFirst = firstNameOf(details.requesterName);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: dense ? 5 : 7 }}>
      <Fact label="When" color="var(--sky-deep)" dense={dense} emphasize>
        {roomRequestWhenLabel(details)}
      </Fact>
      <Fact label="Where" color="var(--lime-deep)" dense={dense}>
        {resolvedRoomName}
        <div
          style={{
            marginTop: 1,
            fontSize: dense ? 10.5 : 11.5,
            fontWeight: 600,
            color: 'var(--ink-soft)',
          }}
        >
          {whereNote ?? 'Nothing is booked yet. Kim needs to approve it and may choose a different room.'}
        </div>
      </Fact>
      <Fact label="What" color="var(--vio-text)" dense={dense}>
        {details.purpose}
        {offeringName ? ` for ${offeringName}` : ''}
        <span style={{ color: 'var(--ink-soft)', fontWeight: 600 }}> · {kindWords}</span>
      </Fact>
      {/* WHY always renders - Bobbie's ask was verbatim "When, Where, WHat
          Why, WHO", all five words, every card. When there is no typed note
          the purpose IS the why (that is the whole reason the request
          exists), so it repeats here rather than leaving a labelled row
          empty - never render a "Why" label with nothing under it. */}
      <Fact label="Why" color="var(--amber-text)" dense={dense}>
        {details.note ? (
          <>
            &ldquo;{details.note}&rdquo;
            <div
              style={{
                marginTop: 1,
                fontSize: dense ? 10.5 : 11.5,
                fontWeight: 600,
                color: 'var(--ink-soft)',
              }}
            >
              What {requesterFirst} typed
            </div>
          </>
        ) : (
          details.purpose
        )}
      </Fact>
      {/* WHO: --info-text, not --rose-deep - Tasks.tsx's "Past due" pill
          also uses rose-deep, and an overdue request would have put the
          requester's own name in the same danger red as the warning right
          above it on the same card. --info-text stays a distinct, non-alarm
          accent (measured 4.5:1+ on every surface, see RoomRequestFacts
          contrast notes in the PR). */}
      <Fact label="Who" color="var(--info-text)" dense={dense} emphasize>
        {details.requesterName}
      </Fact>
      {showInstruction && (
        <div
          style={{
            marginTop: dense ? 2 : 4,
            fontSize: dense ? 11 : 12,
            fontWeight: 700,
            color: 'var(--ink-soft)',
            lineHeight: 1.4,
          }}
        >
          {instruction ?? (
            <>
              Manage it under Calendar &gt; Room requests. Booking it there is the only step
              that actually reserves the room.
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RoomRequestFacts;
